/* Cliente REST da gestão de staff (ADR-0709). Só super_admin consome `/v1/admin/staff`
   (cookie + RBAC no admin-service). As mutações mapeiam os 409 de negócio
   (email_taken/last_super_admin/cannot_suspend_self) para um `ApiError` específico — o
   `staffErrorMessage` (staff-error.ts) traduz cada código para pt-BR acionável. */
import {
  type BulkStaffStatusBody,
  type BulkStaffStatusResponse,
  bulkStaffStatusResponseDto,
  type CreateStaffBody,
  type StaffRole,
  type StaffStatus,
  type StaffUserDto,
  staffUserDto,
} from '@planuze/admin-contracts';
import {
  ApiError,
  type ApiErrorCode,
  buildQuery,
  cursorPageSchema,
  HTTP,
  parseJson,
  readErrorCode,
  request,
} from './http.js';

/* Códigos de negócio 409 repassados como `code` do ApiError (o serverCode preserva o cru). */
const STAFF_CONFLICT_CODES: ReadonlySet<string> = new Set([
  'email_taken',
  'last_super_admin',
  'cannot_suspend_self',
]);

async function staffError(res: Response): Promise<ApiError> {
  const code = await readErrorCode(res);
  if (res.status === HTTP.conflict && STAFF_CONFLICT_CODES.has(code)) {
    return new ApiError(res.status, code as ApiErrorCode, code, code);
  }
  if (res.status === HTTP.unauthorized || res.status === HTTP.forbidden) {
    return new ApiError(res.status, 'unauthorized', code || 'forbidden', code);
  }
  if (res.status === HTTP.notFound) {
    return new ApiError(res.status, 'not_found', code || 'not found', code);
  }
  return new ApiError(res.status, 'server_error', code || 'request failed', code);
}

interface StaffQuery {
  readonly cursor?: string | undefined;
}

const staffPageSchema = cursorPageSchema(staffUserDto);

/** GET /v1/admin/staff — página keyset (`items` + `nextCursor`). Espelha `listCompanies`. */
export async function listStaff(
  query: StaffQuery = {},
): Promise<{ readonly items: readonly StaffUserDto[]; readonly nextCursor: string | null }> {
  const res = await request(`/v1/admin/staff${buildQuery({ cursor: query.cursor })}`);
  if (!res.ok) throw await staffError(res);
  const page = await parseJson(res, staffPageSchema);
  return { items: page.items, nextCursor: page.nextCursor ?? null };
}

export async function createStaff(body: CreateStaffBody): Promise<StaffUserDto> {
  const res = await request('/v1/admin/staff', { method: 'POST', body });
  if (!res.ok) throw await staffError(res);
  return parseJson(res, staffUserDto);
}

export async function updateStaffRole(id: string, role: StaffRole): Promise<StaffUserDto> {
  const res = await request(`/v1/admin/staff/${encodeURIComponent(id)}/role`, {
    method: 'PATCH',
    body: { role },
  });
  if (!res.ok) throw await staffError(res);
  return parseJson(res, staffUserDto);
}

export async function updateStaffStatus(id: string, status: StaffStatus): Promise<StaffUserDto> {
  const res = await request(`/v1/admin/staff/${encodeURIComponent(id)}/status`, {
    method: 'PATCH',
    body: { status },
  });
  if (!res.ok) throw await staffError(res);
  return parseJson(res, staffUserDto);
}

export async function resetStaffPassword(id: string, password: string): Promise<void> {
  const res = await request(`/v1/admin/staff/${encodeURIComponent(id)}/reset-password`, {
    method: 'POST',
    body: { password },
  });
  if (!res.ok) throw await staffError(res);
}

export async function bulkStaffStatus(body: BulkStaffStatusBody): Promise<BulkStaffStatusResponse> {
  const res = await request('/v1/admin/staff/bulk', { method: 'POST', body });
  if (!res.ok) throw await staffError(res);
  return parseJson(res, bulkStaffStatusResponseDto);
}
